/**
 * Equipment warranty + project rollup math (PRD v2 §6.7–6.8). Pure, because both answers
 * decide money: who eats the call, and whether the build made anything.
 */

export type WarrantyKind = 'parts' | 'labor' | 'both' | 'none';

export interface WarrantyStatus {
  kind: WarrantyKind;
  /** Whether anything is still in force — what the badge keys on. */
  covered: boolean;
  /** The soonest cover to lapse, so "expires in 12 days" can be said out loud. */
  expiresOn: string | null;
  daysLeft: number | null;
  /** True when cover ran out inside the last 90 days — the conversation worth having. */
  recentlyExpired: boolean;
}

const RECENTLY_DAYS = 90;

/** Whole days between two ISO dates, positive when `to` is later. UTC to dodge DST. */
function daysBetween(fromIso: string, toIso: string): number {
  const a = Date.parse(`${fromIso}T00:00:00Z`);
  const b = Date.parse(`${toIso}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round((b - a) / 86_400_000);
}

/**
 * Is this unit still covered, and by what (§6.7)? Both dates are inclusive — a warranty
 * that "expires today" still covers today's call, which is the reading a customer holding
 * the paperwork will insist on and the one a shop should lose gracefully.
 *
 * When both covers are live the answer is 'both', and the date reported is the FIRST to
 * lapse: the useful sentence is "you're covered until March", not "until 2031 for one of
 * two things".
 */
export function warrantyStatus(
  today: string,
  partsExpiresOn: string | null,
  laborExpiresOn: string | null,
): WarrantyStatus {
  const partsDays = partsExpiresOn ? daysBetween(today, partsExpiresOn) : null;
  const laborDays = laborExpiresOn ? daysBetween(today, laborExpiresOn) : null;
  const partsLive = partsDays !== null && partsDays >= 0;
  const laborLive = laborDays !== null && laborDays >= 0;

  if (partsLive || laborLive) {
    const live: { on: string; days: number }[] = [];
    if (partsLive) live.push({ on: partsExpiresOn!, days: partsDays! });
    if (laborLive) live.push({ on: laborExpiresOn!, days: laborDays! });
    const soonest = live.reduce((a, b) => (b.days < a.days ? b : a));
    return {
      kind: partsLive && laborLive ? 'both' : partsLive ? 'parts' : 'labor',
      covered: true,
      expiresOn: soonest.on,
      daysLeft: soonest.days,
      recentlyExpired: false,
    };
  }

  // Nothing live. If something lapsed lately, say so — "your warranty ran out in May" is
  // the difference between a surprise invoice and an understood one.
  const lapsed: { on: string; days: number }[] = [];
  if (partsDays !== null) lapsed.push({ on: partsExpiresOn!, days: partsDays });
  if (laborDays !== null) lapsed.push({ on: laborExpiresOn!, days: laborDays });
  if (lapsed.length === 0) {
    return {
      kind: 'none',
      covered: false,
      expiresOn: null,
      daysLeft: null,
      recentlyExpired: false,
    };
  }
  const latest = lapsed.reduce((a, b) => (b.days > a.days ? b : a));
  return {
    kind: 'none',
    covered: false,
    expiresOn: latest.on,
    daysLeft: latest.days,
    recentlyExpired: latest.days >= -RECENTLY_DAYS,
  };
}

/** Age in whole years, for "that unit is 14 years old" — the replacement conversation. */
export function ageInYears(today: string, installedOn: string | null): number | null {
  if (!installedOn) return null;
  const days = daysBetween(installedOn, today);
  if (days < 0) return null; // installed in the future is a typo, not an age
  return Math.floor(days / 365.25);
}

export interface ProjectRollupInput {
  /** Parts consumed at moving average + every other job cost, across the project's jobs. */
  partsCostCents: number;
  otherCostCents: number;
  /** Labor from timers, at the rate snapshotted when each entry was written. */
  laborCostCents: number;
  /**
   * Value still owed on OPEN purchase orders — money committed but not yet a cost. Kept
   * separate on purpose: spending it is a decision already made, but counting it as cost
   * would double-count the moment those parts get consumed on a job.
   */
  committedCents: number;
  /** Issued invoices only. A draft invoice is a document, not revenue. */
  invoicedCents: number;
  /** Approved quotes, as the contract value to measure against. */
  quotedCents: number;
}

export interface ProjectRollup extends ProjectRollupInput {
  costToDateCents: number;
  /** Cost plus what's committed — what the build will have cost if nothing else changes. */
  projectedCostCents: number;
  marginCents: number;
  marginPct: number | null;
  /** Which number the margin is measured against, named out loud. */
  basis: 'invoiced' | 'quoted' | 'none';
  revenueCents: number;
}

/**
 * The project P&L (§6.8). Same doctrine as job costing: the server computes it, names its
 * revenue basis, and never flatters. Committed PO value is reported ALONGSIDE cost rather
 * than inside it — the money is spoken for, but calling it spent today and again when the
 * parts get used would count it twice.
 */
export function computeProjectRollup(input: ProjectRollupInput): ProjectRollup {
  const costToDateCents =
    input.partsCostCents + input.otherCostCents + input.laborCostCents;
  const basis: ProjectRollup['basis'] =
    input.invoicedCents > 0 ? 'invoiced' : input.quotedCents > 0 ? 'quoted' : 'none';
  const revenueCents =
    basis === 'invoiced'
      ? input.invoicedCents
      : basis === 'quoted'
        ? input.quotedCents
        : 0;
  const marginCents = revenueCents - costToDateCents;
  return {
    ...input,
    costToDateCents,
    projectedCostCents: costToDateCents + input.committedCents,
    marginCents,
    // No revenue means no margin to state — 0% would read as "we made nothing", which is a
    // different claim from "nothing has been billed yet".
    marginPct: revenueCents > 0 ? (marginCents / revenueCents) * 100 : null,
    basis,
    revenueCents,
  };
}
